/**
 * G-Max live-scan QA event (post-a96fb4ad).
 *
 * Builds the single structured, per-scan summary that the automated G-Max QA
 * monitor consumes. It is derived PURELY from the live-scan session's totals
 * snapshot (plus the session's applied dex ids) — it changes no scan behaviour
 * and makes no gate decision. The session already counted the PR #461 watched
 * gate markers (`totals.gmaxQa`) as the scan ran; this module shapes them into
 * a transport object and computes the user's "gate + apply" urgent condition.
 *
 * Urgent-failure condition (selected by the user, "Gate plus apply"):
 *   alert ONLY when one of the watched gates fired AND the scan still applied
 *   at least one id in the same scan/session context. A gate firing with zero
 *   applies is healthy (the gate did its job); an apply with no watched gate is
 *   healthy (a clean scan). The dangerous case is a watched gate firing yet an
 *   id slipping through to a present write anyway.
 *
 * The app NEVER calls Notion/Perplexity directly — it only POSTs this event to
 * the app's own backend (`/api/dex/gmax-qa-event`). The scheduled monitor reads
 * the backend's admin feed and updates Notion server-side. See routes.ts.
 */

import type {
  DexLiveSessionTotals,
  GmaxQaAggregate,
} from "./dexLiveScanSession";

/** Wire version so the monitor can evolve the shape without guessing. */
export const GMAX_QA_EVENT_VERSION = 1 as const;

/**
 * The structured G-Max QA scan event. One per finished live scan. Field set is
 * intentionally flat + JSON-primitive so it serialises cleanly to the backend
 * and into a Notion row downstream.
 */
export interface GmaxQaScanEvent {
  /** Schema version of this payload. */
  v: typeof GMAX_QA_EVENT_VERSION;
  /** Client-generated session/scan id (stable for the whole scan). */
  scanId: string;
  /** ms epoch the event was built (scan end). */
  ts: number;
  /** App/build/head identifier if the client knows one, else null. */
  build: string | null;
  /** Most-recent frame variant (e.g. "gmax"), or null. */
  variant: string | null;
  /** Region label derived from the scan (last frame / committed ids). */
  region: string;
  /** Most-recent frame's screen mode (e.g. "grid_4x4"). */
  mode: string;
  /** Most-recent frame's OCR confidence. */
  confidence: number;
  /** Frames the session observed (any mode). */
  framesObserved: number;
  /** Frames that passed the gate and were variant-applied. */
  framesApplied: number;
  /** Distinct (variant, dexNumber) writes the store applied this scan. */
  applied: number;
  /** Distinct dex ids the session applied, ascending. */
  appliedIds: number[];
  /** PR #461 watched gate markers + the derived gate/apply flags. */
  gates: GmaxQaScanEventGates;
  /** Last committed write context (variant + ids + region), or null. */
  lastCommit: GmaxQaScanEventCommit | null;
  /** Last frame's note / overlay-ish text hint, if any. */
  overlayText: string | null;
  /**
   * Raw debug context so a failing frame can be reconstructed without a second
   * round-trip. Kept small + free of image bytes.
   */
  raw: GmaxQaScanEventRaw;
}

export interface GmaxQaScanEventGates {
  /** Σ present-write blocked (invalid dex member). */
  invalidDexMember: number;
  /** Σ present-write blocked (gmax weak sprite evidence). */
  gmaxWeakSprite: number;
  /** Σ gmax weak evidence deferred — plausible catches routed to staged confirm. */
  gmaxWeakDeferred: number;
  /** Σ gmax weak evidence pending — weak (slot,id) sightings still building. */
  gmaxWeakPending: number;
  /** Σ gmax weak evidence confirmed — weak (slot,id) sightings that applied. */
  gmaxWeakConfirmed: number;
  /** Σ frame present-writes blocked (overlay text). */
  overlayTextBlock: number;
  /** Σ (slot,id) present sightings held for a second corroborating frame. */
  twoFramePending: number;
  /** Σ (slot,id) present sightings that cleared two-frame corroboration. */
  twoFrameConfirmed: number;
  /** True if ANY watched gate fired this scan. */
  watchedGateFired: boolean;
  /** True if the scan applied ≥1 id. */
  didApply: boolean;
  /**
   * THE urgent condition: a watched gate fired AND the scan still applied ids.
   * The monitor alerts only on this. Everything else is informational.
   */
  gatePlusApplyUrgent: boolean;
}

export interface GmaxQaScanEventCommit {
  variant: string;
  region: string;
  regionLabel: string;
  ids: number[];
}

export interface GmaxQaScanEventRaw {
  /** Cumulative cells observed across gated frames. */
  cellsObserved: number;
  /** Cumulative dex numbers parsed across gated frames. */
  numbersRead: number;
  /** Cells the OCR returned with no dex number. */
  unknownCells: number;
  /** Writes skipped because a manual override owned the entry. */
  skippedManual: number;
  /** Whether the session ever resolved a confident variant. */
  resolvedVariant: boolean;
  /** Distinct variants the session touched. */
  variantsTouched: string[];
  /** Full per-reason gated counts (diagnostics), for frame-failure debugging. */
  gated: DexLiveSessionTotals["gated"];
}

/**
 * Compute the "gate + apply" urgent flag from a QA aggregate + applied count.
 * Exported so the backend and tests can apply the EXACT same rule without
 * re-deriving it from individual fields.
 */
export function isGatePlusApplyUrgent(
  qa: Pick<GmaxQaAggregate, "watchedGateFired">,
  appliedCount: number,
): boolean {
  return qa.watchedGateFired === true && appliedCount > 0;
}

/**
 * Build the structured QA event from a session totals snapshot + the session's
 * applied dex ids. `scanId` is the caller's stable id for the scan; `build` is
 * the optional app/build/head string the client knows (or null).
 */
export function buildGmaxQaScanEvent(args: {
  scanId: string;
  totals: DexLiveSessionTotals;
  appliedIds: number[];
  build?: string | null;
  ts?: number;
}): GmaxQaScanEvent {
  const { scanId, totals, appliedIds } = args;
  const qa = totals.gmaxQa;
  const didApply = totals.applied > 0;
  const gatePlusApplyUrgent = isGatePlusApplyUrgent(qa, totals.applied);

  return {
    v: GMAX_QA_EVENT_VERSION,
    scanId,
    ts: args.ts ?? Date.now(),
    build: args.build ?? null,
    variant: totals.lastVariant,
    region: totals.lastRegion,
    mode: totals.lastMode,
    confidence: totals.lastConfidence,
    framesObserved: totals.framesObserved,
    framesApplied: totals.framesApplied,
    applied: totals.applied,
    appliedIds: [...appliedIds],
    gates: {
      invalidDexMember: qa.invalidDexMember,
      gmaxWeakSprite: qa.gmaxWeakSprite,
      gmaxWeakDeferred: qa.gmaxWeakDeferred,
      gmaxWeakPending: qa.gmaxWeakPending,
      gmaxWeakConfirmed: qa.gmaxWeakConfirmed,
      overlayTextBlock: qa.overlayTextBlock,
      twoFramePending: qa.twoFramePending,
      twoFrameConfirmed: qa.twoFrameConfirmed,
      watchedGateFired: qa.watchedGateFired,
      didApply,
      gatePlusApplyUrgent,
    },
    lastCommit: totals.lastCommit
      ? {
          variant: totals.lastCommit.variant,
          region: totals.lastCommit.region,
          regionLabel: totals.lastCommit.regionLabel,
          ids: [...totals.lastCommit.ids],
        }
      : null,
    overlayText: totals.lastNote ?? null,
    raw: {
      cellsObserved: totals.cellsObserved,
      numbersRead: totals.numbersRead,
      unknownCells: totals.unknownCells,
      skippedManual: totals.skippedManual,
      resolvedVariant: totals.resolvedVariant,
      variantsTouched: [...totals.variantsTouched],
      gated: { ...totals.gated },
    },
  };
}
